import {inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {AssociationType} from "../../models/association-type";
import {Directory, Filesystem} from "@capacitor/filesystem";
import {LocalFile} from "../../models/local-file.model";
import {Photo} from "@capacitor/camera";
import {OfflineAbstractService} from "../offline-abstract.service";
import {AssociationKey} from "../../models/association-key.model";
import {AssociatedPhoto} from "../../models/associated-photo.model";
import {Entity} from '../entity.interface';
import {QueryParams, QueryResult} from '../service.interface';
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {LoggerService} from "../logging/logger.service";
import {withWriteTimeout} from "../timeout/write-timeout.interceptor";
import {DraftPhoto} from "../../models/event-draft.model";

const IMAGE_DIR = 'images';

// A healthy multi-photo upload can legitimately exceed the 30s default
// write bound (HEC-347); the activity save it is keyed to is already
// committed by the time this runs (see event-form.component.ts), so a
// longer, more forgiving bound is appropriate here.
const PHOTO_WRITE_TIMEOUT_MS = 120000;


@Injectable({
  providedIn: 'root'
})
export class PhotoService extends OfflineAbstractService<
  AssociationKey<any>, AssociatedPhoto<any>
> {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  private readonly photoUpdatedForEvent =
    new BehaviorSubject<string>('');
  readonly photoUpdatedForEvent$ =
    this.photoUpdatedForEvent.asObservable();

  private static readonly TTL_SECONDS = 30 * 60;

  constructor() {
    super(false, PhotoService.TTL_SECONDS);
  }

  public async initialize(): Promise<void> {
    await this.checkAndCreateDirectories(
      [IMAGE_DIR, `${IMAGE_DIR}/${AssociationType.EVENT}`]
    );
    this.logger.debug(
      'PhotoService initialized - directories created'
    );
  }

  public async getPhotosForEvent(eventId: string) {
    return await this.find(
      AssociationKey.createEventKey(eventId)
    );
  }

  public async getAssociatedPhoto(
    associationId: string,
    associationType: AssociationType
  ) {
    return await this.find(
      AssociationKey.createAssociationKey(
        associationType, associationId
      )
    );
  }

  /**
   * Re-reads the photos a persisted draft refers to (HEC-178). Each photo
   * is read from the device filesystem at its event path; when the file is
   * missing and allowServer is true it is fetched from the server and
   * re-saved (an Edit draft's saved photos may have lost their local files
   * to the lazy AssociatedPhoto cache expiry). Photos found nowhere are
   * dropped with a warning. allowServer is false for an Add draft: the
   * server has never seen its id, so a miss must not cost a 404 toast.
   */
  public async readLocalPhotos(
    eventId: string,
    photos: DraftPhoto[],
    allowServer: boolean
  ): Promise<LocalFile[]> {
    const association = new AssociatedPhoto<string>(
      AssociationKey.createEventKey(eventId), []
    );
    const found = await Promise.all(photos.map(async ({name, path}) => {
      try {
        const data = allowServer
          ? await this.readFromFileOrServer({name, path, data: ''}, association)
          : await this.readLocalFile(AssociationType.EVENT, eventId, name);
        if (data) {
          return {name, path, data};
        }
      } catch (error) {
        this.logger.warn('Error reading draft photo', name, error);
      }
      this.logger.warn('Dropping draft photo with no readable file', name);
      return null;
    }));
    return found.filter((photo): photo is LocalFile => photo !== null);
  }

  private async readLocalFile(
    type: AssociationType, id: string, fileName: string
  ): Promise<string> {
    const result = await Filesystem.readFile({
      directory: Directory.Data,
      path: this.getFileSystemPath(type, id, fileName)
    });
    return result.data as string;
  }

  public async savePicture(
    cameraPhoto: Photo,
    type: AssociationType,
    id: string
  ) {
    const base64Data = cameraPhoto.base64String;
    if (base64Data) {
      const fileName = this.createFileName();
      return await this.saveFile(
        base64Data, fileName, type, id
      );
    }
    this.logger.error('No base64 data in photo');
    return null;
  }

  public async deletePhoto(
    file: LocalFile,
    associationType: AssociationType,
    associationId: string
  ) {
    try {
      const localPath = this.getFileSystemPath(
        associationType, associationId, file.name
      );
      return await Filesystem.deleteFile({
        directory: Directory.Data,
        path: localPath
      });
    } catch (error) {
      this.logger.warn(
        'Photo has probably been removed from the cache',
        error
      );
    }
  }

  protected override onCacheExpired(
    item: AssociatedPhoto<any>
  ): void {
    this.deleteEventPhotosFromFilesystem(
      item.id.associationType, item.id.associationId
    );
  }

  private deleteEventPhotosFromFilesystem(
    associationType: AssociationType,
    associationId: string
  ): void {
    const directory = this.getDirectoryName(
      associationType, associationId
    );
    Filesystem.readdir({
      directory: Directory.Data,
      path: directory
    }).then(contents => {
      contents.files.forEach(file => {
        Filesystem.deleteFile({
          directory: Directory.Data,
          path: this.getFileSystemPath(
            associationType, associationId, file.name
          )
        }).catch(err => this.logger.warn(
          `Failed to delete: ${file.name}`, err
        ));
      });
    }).catch(() => {
      // Directory doesn't exist - nothing to clean
    });
  }

  private cleanupOrphanedFiles(
    associationType: AssociationType,
    associationId: string,
    authorisedPhotoNames: string[]
  ): void {
    const directory = this.getDirectoryName(
      associationType, associationId
    );
    Filesystem.readdir({
      directory: Directory.Data,
      path: directory
    }).then(contents => {
      const authorisedSet =
        new Set(authorisedPhotoNames);
      contents.files
        .filter(file => !authorisedSet.has(file.name))
        .forEach(file => {
          this.logger.debug(
            `Deleting orphaned file: ${file.name}`
          );
          Filesystem.deleteFile({
            directory: Directory.Data,
            path: this.getFileSystemPath(
              associationType, associationId, file.name
            )
          }).catch(err => this.logger.warn(
            `Failed to delete orphan: ${file.name}`, err
          ));
        });
    }).catch(() => {
      // Directory doesn't exist - nothing to clean
    });
  }

  override get type(): string {
    return 'AssociatedPhoto';
  }

  override getUrl(
    id?: AssociationKey<any> | undefined
  ): string {
    if (id) {
      return `${this.appConfigService.getPhotoUrl()}`
        + `/${id.associationType}/${id.associationId}`;
    }
    throw new Error(
      'Unable to get URL for photos without an id'
    );
  }

  override delete(
    id: AssociationKey<any>
  ): Promise<void> {
    throw new Error('Method not implemented.');
  }

  override get(
    id: AssociationKey<any>
  ): Promise<AssociatedPhoto<any>> {
    return firstValueFrom(
      this.http.get<AssociatedPhoto<any>>(
        this.getUrl(id)
      ).pipe(
        map((data) => {
          if (data) {
            data.type = 'AssociatedPhoto';
          }
          return data;
        }),
        catchError(() => {
          this.logger.warn(
            'Error fetching photos for association'
          );
          return Promise.resolve(
            new AssociatedPhoto(id, [])
          );
        })
      )
    );
  }

  /**
   * Finds an event's photos, populating binary data
   * from the filesystem or server as needed.
   */
  override async find(
    id: AssociationKey<any>
  ): Promise<AssociatedPhoto<any> | null> {
    let photos: AssociatedPhoto<any> | null =
      await this.cachingService.find(id);

    if (photos) {
      photos = await this.populatePhotos(photos);
    }

    return photos;
  }

  private async populatePhotos(
    associatedPhotos: AssociatedPhoto<any>
  ) {
    const photoPromises = associatedPhotos.photos
      .filter(photo => !photo.data)
      .map(async (photo) => {
        const base64: string =
          await this.readFromFileOrServer(
            photo, associatedPhotos
          );
        if (base64) {
          photo.data = base64;
        } else {
          this.logger.error(
            'No data found from file or server'
          );
        }
      });

    await Promise.all(photoPromises);
    return associatedPhotos;
  }

  private async readFromFileOrServer(
    photo: LocalFile,
    associatedPhotos: AssociatedPhoto<any>
  ) {
    const localPath = this.getFileSystemPath(
      associatedPhotos.id.associationType,
      associatedPhotos.id.associationId,
      photo.name
    );

    try {
      const result = await Filesystem.readFile({
        directory: Directory.Data,
        path: localPath
      });
      return result.data as string;
    } catch (err: any) {
      if (
        err.message.includes('does not exist')
        || err.message.includes(
          'there is no such file'
        )
      ) {
        const base64 =
          await this.readPhotoBytesFromServer(
            photo, associatedPhotos
          );
        if (base64) {
          await this.saveFile(
            base64,
            photo.name,
            associatedPhotos.id.associationType,
            associatedPhotos.id.associationId
          );
          return base64;
        }
      }
      this.logger.error(
        'Ignoring error reading file or server data',
        err
      );
      return '';
    }
  }

  private getFileSystemPath(
    type: AssociationType, id: string, fileName: string
  ) {
    return `${this.getDirectoryName(type, id)}`
      + `/${fileName}`;
  }

  private async readPhotoBytesFromServer(
    photo: LocalFile,
    associatedPhotos: AssociatedPhoto<any>
  ) {
    const url = `${this.appConfigService.getPhotoUrl()}`
      + `/${associatedPhotos.id.associationType}`
      + `/${associatedPhotos.id.associationId}`
      + `/${photo.name}`;
    return firstValueFrom(
      this.http.get(url, { responseType: 'blob' }).pipe(
        map(async (blob) => {
          return await PhotoService.blobToBase64(blob);
        })
      )
    );
  }

  override getAll(): Promise<AssociatedPhoto<any>[]> {
    throw new Error('Method not implemented.');
  }

  override async post(
    item: AssociatedPhoto<any>
  ): Promise<AssociatedPhoto<any>> {
    const formData = await PhotoService.toFormData(item);
    await firstValueFrom(
      this.http.post(this.getUrl(item.id), formData, {
        context: withWriteTimeout(PHOTO_WRITE_TIMEOUT_MS)
      })
    );
    item.photos = this.removeBinaryData(item.photos);
    return item;
  }

  private removeBinaryData(photos: LocalFile[]) {
    return photos.map(p => ({
      name: p.name,
      path: p.path,
      data: ''
    }));
  }

  override async update(
    item: AssociatedPhoto<any>,
    params?: Map<string, string> | undefined,
    rethrowOnError = false
  ): Promise<AssociatedPhoto<any>> {
    const result = await super.update(item, params, rethrowOnError);

    this.cleanupOrphanedFiles(
      item.id.associationType,
      item.id.associationId,
      result.photos.map(p => p.name)
    );

    this.photoUpdatedForEvent.next(
      result.id.associationId
    );
    return result;
  }

  // rethrowOnError forwarded exactly as update() forwards it just
  // above (see api/README.md's write-path note, HEC-347) - a create()
  // override that narrowed to (item, params) would silently drop a
  // future caller's rethrowOnError with no compile error.
  override async create(
    item: AssociatedPhoto<any>,
    params?: Map<string, string> | undefined,
    rethrowOnError = false
  ): Promise<AssociatedPhoto<any>> {
    const result = await super.create(item, params, rethrowOnError);

    this.cleanupOrphanedFiles(
      item.id.associationType,
      item.id.associationId,
      result.photos.map(p => p.name)
    );

    this.photoUpdatedForEvent.next(
      item.id.associationId
    );
    return result;
  }

  override async put(
    item: AssociatedPhoto<any>
  ): Promise<AssociatedPhoto<any>> {
    const formData = await PhotoService.toFormData(item);
    await firstValueFrom(
      this.http.put(this.getUrl(item.id), formData, {
        context: withWriteTimeout(PHOTO_WRITE_TIMEOUT_MS)
      })
    );
    item.photos = this.removeBinaryData(item.photos);
    return item;
  }

  public static async toFormData(
    item: AssociatedPhoto<any>
  ) {
    const formData = new FormData();
    formData.append('id', item.id.associationId);
    formData.append(
      'type', item.id.associationType as string
    );
    let metaData = [];
    for (let photo of item.photos) {
      const blob = await this.base64ToBlob(photo.data);
      formData.append('images', blob, photo.name);
      metaData.push({
        name: photo.name,
        path: photo.path
      });
    }
    formData.append('meta', JSON.stringify(metaData));
    return formData;
  }

  public static async fromFormData(
    formData: FormData
  ): Promise<AssociatedPhoto<any>> {
    let associationId: string =
      formData.get('id') as string;
    let associationType: AssociationType =
      formData.get('type') as AssociationType;
    let associationKey =
      AssociationKey.createAssociationKey(
        associationType, associationId
      );
    let images: File[] =
      formData.getAll('images') as File[];
    let imageMap = images.reduce((map, image) => {
      map[image.name] = image;
      return map;
    }, {} as { [key: string]: File });
    let localFiles: LocalFile[] =
      JSON.parse(formData.get('meta') as string);
    let photos = localFiles.map(async localFile => ({
      name: localFile.name,
      path: localFile.path,
      data: await PhotoService.blobToBase64(
        imageMap[localFile.name]
      )
    }));
    let resolvedPhotos: LocalFile[] =
      await Promise.all(photos);
    return new AssociatedPhoto<any>(
      associationKey, resolvedPhotos
    );
  }

  override async query<
    D extends Entity<AssociationKey<any>>
  >(
    query: QueryParams
  ): Promise<QueryResult<AssociationKey<any>, D>> {
    return Promise.reject(
      new Error('Unknown query type')
    );
  }

  override sort(
    items: AssociatedPhoto<any>[]
  ): AssociatedPhoto<any>[] {
    throw new Error('Method not implemented.');
  }

  override validate(
    item: AssociatedPhoto<any>
  ): string {
    return '';
  }

  private static async base64ToBlob(
    base64: string
  ): Promise<Blob> {
    const response =
      await fetch(this.ensureBase64URI(base64));
    return await response.blob();
  }

  public static ensureBase64URI(data: string) {
    if (data) {
      if (data.startsWith('data:image/jpeg;base64,')) {
        return data;
      } else {
        return 'data:image/jpeg;base64,' + data;
      }
    }
    return '';
  }

  private async checkAndCreateDirectories(
    directory: string[]
  ) {
    for (let d of directory) {
      await this.checkAndCreateDirectory(d);
    }
  }

  private async checkAndCreateDirectory(
    directory: string
  ) {
    try {
      await Filesystem.mkdir({
        directory: Directory.Data,
        path: directory,
        recursive: true
      });
    } catch (err: any) {
      if (err?.message?.includes('already exist')) {
        this.logger.debug(
          `Directory already exists: ${directory}`
        );
        return;
      }
      throw err;
    }
  }

  private createFileName() {
    const date = new Date();
    const time = date.getTime();
    return `${time}.jpeg`;
  }

  private async saveFile(
    base64Data: string,
    fileName: string,
    type: AssociationType,
    id: string
  ): Promise<LocalFile> {
    await this.checkAndCreateDirectory(
      this.getDirectoryName(type, id)
    );
    const path = this.getFileSystemPath(
      type, id, fileName
    );
    this.logger.info('Writing file to ', path);
    await Filesystem.writeFile({
      directory: Directory.Data,
      path: path,
      data: base64Data
    });
    return {
      name: fileName,
      path: path,
      data: base64Data
    };
  }

  private static async blobToBase64(
    blob: Blob
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(blob);
    });
  }

  private getDirectoryName(
    type: AssociationType, id: string
  ) {
    return `${IMAGE_DIR}/${type}/${id}`;
  }
}
